import Swiper, {
    Navigation,
    Pagination,
    Autoplay,
    EffectCoverflow,
    EffectCards,
} from "swiper";
import "swiper/css";
import http from "@/http";
import { setTimeZone, realTimeUpdateCountryTimeZones } from "@/timezone";
import { getJourneyContext } from "@/journey";
import Gumshoe from "gumshoejs";

Swiper.use([Navigation, Pagination, Autoplay, EffectCoverflow, EffectCards]);
setTimeZone("Europe/Kiev");

if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
}

const siteWrapper = document.body;

const handleLinkClick = (e) => {
    const anchor = e.target.closest('a');
    if (anchor) {
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
            || anchor.target === '_blank' || anchor.hasAttribute('download')) {
            return;
        }

        e.preventDefault();
        const href = anchor.href;
        if (location.href !== href) {
            window.location = href;
        }
    }
};

const closeModal = () => {
    siteWrapper.classList.remove("is-modal-open");
    document.querySelectorAll(".modal").forEach((modal) => {
        modal.classList.remove("active");
    });
};

const openModal = (modalId, content = null) => {
    const modal = document.getElementById(modalId);
    if (!modal) {
        window.addEventListener("hotvisa:forms-ready", () => openModal(modalId, content), { once: true });
        window.dispatchEvent(new CustomEvent("hotvisa:load-forms"));
        return;
    }
    if (content) {
        const title = modal.querySelector('.modal-title');
        const description = modal.querySelector('.modal-descr');
        if (title && content.title !== null) title.textContent = content.title;
        if (description && content.description !== null) description.textContent = content.description;
    }
    siteWrapper.classList.add("is-modal-open");
    modal.classList.add("active");
};

const setupModalButtons = () => {
    // The lazy Vue mount replaces #app's children, including every CTA. Keep
    // the listener on the body so new buttons and overlays still work afterward.
    siteWrapper.addEventListener("click", (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest(".modal-close, .modal-overlay")) {
            closeModal();
            return;
        }

        const button = event.target.closest("[data-modal-btn], [consult-banner-modal-btn]");
        if (!button) return;
        switch (button.getAttribute("data-modal-btn")) {
            case "join":
                openModal("modal-join");
                break;
            case "consult":
                openModal("modal-consult");
                break;
            case "custom-consult":
                openModal("modal-consult", {
                    title: button.getAttribute("data-form-name"),
                    description: button.getAttribute("data-form-description"),
                });
                break;
            default:
                if (button.hasAttribute("consult-banner-modal-btn")) {
                    openModal("modal-banner-consult");
                }
        }
    });
};

const setupMobileMenu = () => {
    const headerDiv = document.querySelector("header");
    if (!headerDiv) return;

    const burger = headerDiv.querySelector(".burger-btn");
    const mobileMenu = headerDiv.querySelector(".mobile-menu .menu");
    const mobileMenuDropdownOpener = headerDiv.querySelector(".mobile-menu-dropdown-opener");
    const mobileMenuDropdownReturn = headerDiv.querySelector(".mobile-menu-dropdown-return");
    const dropdownCategories = headerDiv.querySelectorAll(".mobile-menu-dropdown-category");
    const dropdownCategoriesToggles = headerDiv.querySelectorAll(".mobile-menu-dropdown-category-title");

    burger.addEventListener("click", () => {
        siteWrapper.classList.toggle("mobile-menu-open");
        mobileMenu.classList.remove("is-dropdown-active");
        dropdownCategories.forEach((dropdownCategory) => {
            dropdownCategory.classList.remove("is-expanded");
        });
    });

    mobileMenuDropdownOpener.addEventListener("click", () => {
        mobileMenu.classList.add("is-dropdown-active");
    });

    mobileMenuDropdownReturn.addEventListener("click", () => {
        mobileMenu.classList.remove("is-dropdown-active");
    });

    dropdownCategoriesToggles.forEach((btn) => {
        btn.addEventListener("click", () => {
            btn.parentElement.classList.toggle("is-expanded");
        });
    });
};

const setupSwiper = (selector, options) => {
    const element = document.querySelector(selector);
    if (element) {
        new Swiper(element, options);
    }
};

const setupFeedbackSwiper = () => {
    const blockFeedback = document.querySelector(".block-feedback");
    if (!blockFeedback) return;

    const swiper = new Swiper("#feedback", {
        init: false,
        slidesPerView: "auto",
        freeMode: true,
        observer: true,
        observeParents: true,
        loop: false,
        centeredSlides: true,
        centeredSlidesBounds: true,
        speed: 1000,
        navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
        },
        pagination: {
            el: ".swiper-pagination",
            type: "bullets",
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 4,
        },
    });

    swiper.on("slideChange afterInit init", function () {
        const currentSlide = this.activeIndex + 1;
        blockFeedback.querySelector(".swiper-counter").innerHTML = `<span class="swiper-counter-current">${currentSlide}</span> / <span class="swiper-counter-total">${this.slides.length}</span>`;
    });

    swiper.init();
};

const setupTimeTicker = () => {
    const timeContainer = document.getElementById("time");
    if (!timeContainer) return;

    const options = {
        timeZone: "Europe/Kiev",
        hour12: false,
        hour: "numeric",
        minute: "2-digit",
    };

    let ticker = true;

    const tick = () => {
        const now = new Date();
        const time = now
          .toLocaleString("en", options)
          .replace(
            ":",
            ticker ? '<span style="visibility: hidden">:</span>' : "<span>:</span>"
          );

        ticker = !ticker;
        timeContainer.innerHTML = `EEST  ( ${time} )`;
    };

    setInterval(tick, 1000);
    tick();
};

const setupCountryTimeZones = () => {
    const listCountryTimeZones = document.querySelectorAll(".country-time-zone");
    if (listCountryTimeZones) {
        listCountryTimeZones.forEach((item) => {
            const timezone = item.dataset.timezone;
            setTimeZone(timezone, `country-timezone-${item.dataset.slug}`, "");
        });
    }

    const pageBanner = document.getElementById("page-banner");
    if (pageBanner) {
        http.get("/api/v1/countries").then((response) => {
            realTimeUpdateCountryTimeZones(response);
        });
    }
};

const setupAccordions = () => {
    const accordion = document.querySelector(".block-accordeon");
    if (!accordion) return;

    const items = accordion.querySelectorAll(".accordeon-item");

    items.forEach((accordionElem, index) => {
        const clickZone = accordionElem.querySelector(".accordeon-item-head");
        clickZone.addEventListener("click", () => {
            items.forEach((item, idx) => {
                if (index !== idx) item.classList.remove("active");
            });
            accordionElem.classList.toggle("active");
        });
    });

    window.addEventListener("scroll", () => {
        items.forEach((item) => {
            const itemTop = item.getBoundingClientRect().top + window.scrollY;
            const itemHeight = item.clientHeight;

            if (
              window.scrollY >= itemTop - itemHeight / 3 &&
              window.scrollY < itemTop + itemHeight + itemHeight / 3
            ) {
                item.classList.add("mobile-active");
            } else {
                item.classList.remove("mobile-active");
            }
        });
    });
};

const setupTabs = (blockSelector, tabSelector, tabContentSelector) => {
    const block = document.querySelector(blockSelector);
    if (!block) return;

    const selectors = block.querySelectorAll(tabSelector);
    const tabs = block.querySelectorAll(tabContentSelector);

    selectors.forEach((selector, i) => {
        selector.setAttribute("selector-index", i);
        selector.addEventListener("click", () => {
            const index = selector.getAttribute("selector-index");
            selectors.forEach((btn) => btn.classList.remove("active"));
            tabs.forEach((tab) => tab.classList.remove("active"));
            selector.classList.add("active");
            tabs[index].classList.add("active");
        });
    });
};

const setupSunAnimation = () => {
    const sun = document.getElementById("sun");
    if (!sun) return;

    window.addEventListener("scroll", () => {
        const scroll = window.scrollY;
        const height = 2200;
        const topFrom = 830;
        const topTo = -2000;
        const px = (scroll / height) * (topFrom - topTo);

        sun.style.top = `${topFrom - px}px`;
        sun.style.zoom = Math.max(0, 1 - scroll / height);
    });
};

const setupAnchorLinks = () => {
    const blockArticle = document.querySelector(".block-article");
    if (!blockArticle) return;

    const header = document.querySelector(".header");
    const offsetFromHeader = header.getBoundingClientRect().height + 48;

    blockArticle.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.addEventListener("click", function (e) {
            e.preventDefault();
            blockArticle
              .querySelectorAll('a[href^="#"]')
              .forEach((a) => a.parentElement.classList.remove("active"));
            this.parentElement.classList.add("active");

            const targetElem = document.querySelector(this.getAttribute("href"));
            const targetElemPosition = targetElem.getBoundingClientRect().top;
            const offsetPosition = targetElemPosition + window.scrollY - offsetFromHeader;

            window.scrollTo(0, offsetPosition);
        });
    });

    const isNav = document.querySelector("#longread-nav");
    if (isNav) {
        new Gumshoe("#longread-nav a", {
            navClass: "active",
            offset: () => offsetFromHeader,
        });
    }
};

const setupAccordion = () => {
    const blockVisaSteps = document.querySelectorAll(".block-visa-steps, .block-faq");
    if (blockVisaSteps && blockVisaSteps.length) {
        for (let blockVisaStep of blockVisaSteps) {
            const listItems = blockVisaStep.querySelectorAll(".steps-list > li");
            listItems.forEach((item) =>
                item.addEventListener("click", () => item.classList.toggle("active"))
            );
        }
    }
};

const cookieConsentKey = "hotvisa_cookie_consent";

const getCookieConsent = () => {
    const cookieMatch = document.cookie.match(new RegExp(`(?:^|; )${cookieConsentKey}=([^;]*)`));
    if (cookieMatch) {
        return decodeURIComponent(cookieMatch[1]);
    }

    try {
        return window.localStorage.getItem(cookieConsentKey);
    } catch (e) {
        return null;
    }
};

const setCookieConsent = (value) => {
    const encodedValue = encodeURIComponent(value);
    document.cookie = `${cookieConsentKey}=${encodedValue}; Max-Age=15552000; Path=/; SameSite=Lax`;

    try {
        window.localStorage.setItem(cookieConsentKey, value);
    } catch (e) {
        // Browsers can disable storage; the first-party cookie above is enough.
    }
};

const setupCookieConsent = () => {
    const banner = document.querySelector("[data-cookie-consent]");
    if (!banner) return;

    if (banner.dataset.openaiMeasurementEnabled === "true") {
        const settings = document.querySelector("[data-openai-consent-settings]");
        const consentUrl = banner.dataset.openaiConsentUrl;
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content || "";
        window.hvOpenAiMeasurement = {
            measurement_consent: "unknown", measurement_consent_at: "",
            openai_measurement_permission: "unknown",
        };
        const applyState = (state) => {
            if (!state || !["granted", "denied", "unknown"].includes(state.measurement_consent)) return;
            window.hvOpenAiMeasurement = state;
            if (state.openai_measurement_permission === "granted") {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({event: "openai_measurement_granted"});
                if (typeof window.oaiq === "function") window.oaiq("consent", true);
            } else if (typeof window.oaiq === "function") {
                window.oaiq("consent", false);
            }
        };
        settings?.addEventListener("click", () => { banner.hidden = false; });
        banner.querySelectorAll("[data-openai-consent-choice]").forEach((button) => {
            button.addEventListener("click", () => {
                const reference = getJourneyContext().acquisition_capture_ref;
                button.disabled = true;
                fetch(consentUrl, {
                    method: "POST", credentials: "same-origin", cache: "no-store",
                    headers: {"Content-Type": "application/json", "X-CSRF-TOKEN": csrf},
                    body: JSON.stringify({choice: button.dataset.openaiConsentChoice,
                        acquisition_capture_ref: reference || null}),
                }).then((response) => {
                    if (!response.ok) throw new Error("Measurement choice unavailable");
                    return response.json();
                }).then((state) => {
                    applyState(state);
                    banner.hidden = true;
                }).catch(() => {
                    banner.hidden = false;
                }).finally(() => { button.disabled = false; });
            });
        });
        banner.hidden = true;
        fetch(consentUrl, {credentials: "same-origin", cache: "no-store"})
            .then((response) => response.ok ? response.json() : null)
            .then((state) => {
                applyState(state);
                banner.hidden = !state || state.measurement_consent !== "unknown";
            }).catch(() => { banner.hidden = false; });
        return;
    }

    // This is an informational notice. Existing choices only dismiss it;
    // collection and retention remain independent of the notice.
    banner.hidden = true;
    if (getCookieConsent()) return;

    const acceptButton = banner.querySelector("[data-cookie-consent-accept]");
    if (acceptButton) {
        acceptButton.addEventListener("click", () => {
            setCookieConsent("accepted");
            banner.hidden = true;
        });
    }

    // Country is resolved separately from response-cached HTML. Neither a
    // failed lookup nor an unknown country should show the notice worldwide.
    fetch(banner.dataset.cookieRegionUrl, { credentials: "omit", cache: "no-store" })
        .then((response) => response.ok ? response.json() : null)
        .then((region) => {
            if (region && region.show_cookie_notice === true && !getCookieConsent()) {
                banner.hidden = false;
            }
        })
        .catch(() => {});
};

const init = () => {
    // Start or refresh the contact journey on every page load. forms.js also
    // does this, but it is loaded lazily (forms-loader.js), so a visitor who
    // lands on one page and contacts on another would otherwise get the later
    // page recorded as landing_path and lose the landing page's capture ref.
    getJourneyContext();

    siteWrapper.addEventListener('click', handleLinkClick);
    setupModalButtons();
    setupMobileMenu();
    // setupFeedbackSwiper();
    setupTimeTicker();
    setupCountryTimeZones();
    setupAccordions();
    setupTabs(".block-visa-types", ".type-selector", ".tab");
    setupTabs(".block-cases", ".case-selector", ".tab");
    setupSunAnimation();
    setupAnchorLinks();
    setupAccordion();
    setupCookieConsent();

    const initTicketCarousel = () => setupSwiper("#tickets", {
        freeMode: true,
        observer: true,
        observeParents: true,
        loop: false,
        centeredSlides: true,
        centeredSlidesBounds: true,
        speed: 1000,
        breakpoints: {
            0: { slidesPerView: 1 },
            768: { slidesPerView: "auto" },
        },
    });
    if ("requestIdleCallback" in window) {
        window.requestIdleCallback(initTicketCarousel, { timeout: 1500 });
    } else {
        window.setTimeout(initTicketCarousel, 250);
    }

    setupSwiper("#team", {
        slidesPerView: "auto",
        freeMode: true,
        observer: true,
        observeParents: true,
        loop: true,
        centeredSlides: true,
        centeredSlidesBounds: true,
        navigation: { prevEl: ".swiper-button-prev", nextEl: ".swiper-button-next" },
        pagination: { el: ".swiper-pagination", type: "bullets" },
    });

    setupSwiper(".block-related-articles .swiper-container", {
        slidesPerView: "auto",
        loop: false,
    });

    setupSwiper(".block-article .swiper-container", {
        slidesPerView: 1,
        loop: true,
        navigation: { prevEl: ".swiper-button-prev", nextEl: ".swiper-button-next" },
        pagination: { el: ".swiper-pagination", type: "bullets" },
    });

    initLanguageSwitcher();
};

document.addEventListener("DOMContentLoaded", init);

function initLanguageSwitcher() {
// Get all language switchers
    const languageSwitchers = document.querySelectorAll('.language-switcher');

    // Apply click handlers to each switcher
    languageSwitchers.forEach(switcher => {
        const currentLanguage = switcher.querySelector('.language-switcher__current');

        if (currentLanguage) {
            // Add click event to toggle dropdown
            currentLanguage.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();

                // Close all other open language dropdowns
                languageSwitchers.forEach(otherSwitcher => {
                    if (otherSwitcher !== switcher) {
                        otherSwitcher.classList.remove('is-open');
                    }
                });

                // Toggle current dropdown
                switcher.classList.toggle('is-open');
            });

            // Add keyboard accessibility
            currentLanguage.setAttribute('tabindex', '0');
            currentLanguage.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    switcher.classList.toggle('is-open');
                }
            });
        }
    });

    // Close all language dropdowns when clicking outside
    document.addEventListener('click', function(event) {
        languageSwitchers.forEach(switcher => {
            if (!switcher.contains(event.target)) {
                switcher.classList.remove('is-open');
            }
        });
    });

    // Handle ESC key to close dropdowns
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            languageSwitchers.forEach(switcher => {
                switcher.classList.remove('is-open');
            });
        }
    });
}
